import React, { useState } from 'react';
import { Download, Mail, Loader2, CheckCircle, AlertCircle, FileText } from 'lucide-react';
import { Card } from '../ui/Card';
import { generatePdfHtml } from './PdfContent';
import { CalculatorOutputs, CalculatorInputs } from '../../types/calculator';

interface PdfExportCardProps {
  scaledOutputs: CalculatorOutputs;
  timePeriod: string;
  inputs: CalculatorInputs;
}

type EmailStatus = 'idle' | 'loading' | 'success' | 'error';

export const PdfExportCard: React.FC<PdfExportCardProps> = ({
  scaledOutputs,
  timePeriod,
  inputs,
}) => {
  const [email, setEmail] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [emailStatus, setEmailStatus] = useState<EmailStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const generatePdf = async (): Promise<string | null> => {
    const html = generatePdfHtml({ scaledOutputs, timePeriod, inputs });

    try {
      const response = await fetch('/api/generate-pdf', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ html }),
      });

      const data = await response.json();
      console.log('PDF API response:', { status: response.status, hasError: !!data.error, hasPdf: !!data.pdf, pdfLength: data.pdf?.length });

      if (!response.ok) {
        console.error('PDF API error:', data.error, data.details);
        throw new Error(data.details || data.error || 'Failed to generate PDF');
      }

      if (!data.pdf) {
        console.error('PDF API returned no PDF data:', data);
        throw new Error('No PDF data returned');
      }

      // Validate base64 format
      if (typeof data.pdf !== 'string' || data.pdf.length === 0) {
        console.error('Invalid PDF data format:', typeof data.pdf);
        throw new Error('Invalid PDF data format');
      }

      return data.pdf; // base64 encoded PDF
    } catch (error) {
      console.error('PDF generation error:', error);
      return null;
    }
  };

  const handleDownloadPdf = async () => {
    setIsDownloading(true);

    try {
      const pdfBase64 = await generatePdf();

      if (!pdfBase64) {
        throw new Error('Failed to generate PDF');
      }

      // Convert base64 to blob and download
      const byteCharacters = atob(pdfBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `workplace-roi-report-${new Date().getDate().toString().padStart(2, '0')}-${(new Date().getMonth() + 1).toString().padStart(2, '0')}-${new Date().getFullYear()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleEmailPdf = async () => {
    // Check if email is entered
    if (!email) {
      setEmailStatus('error');
      setErrorMessage('Please enter an email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailStatus('error');
      setErrorMessage('Please enter a valid email address');
      return;
    }

    setEmailStatus('loading');
    setErrorMessage('');

    try {
      const pdfBase64 = await generatePdf();

      if (!pdfBase64) {
        throw new Error('Failed to generate PDF');
      }

      // Send email with PDF attachment
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          pdfBase64,
          timePeriod,
        }),
      });

      if (response.ok) {
        setEmailStatus('success');
        setEmail('');
        setTimeout(() => setEmailStatus('idle'), 3000);
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to send email');
      }
    } catch (error) {
      setEmailStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Failed to send email');
    }
  };

  return (
    <Card className="mt-8">
      <div className="flex flex-col lg:flex-row lg:items-center gap-6">
        {/* Left section - Title and description (1/3) */}
        <div className="lg:w-1/3">
          <h3 className="text-lg font-serif font-semibold text-offblack mb-2 flex items-center gap-2">
            <FileText className="w-5 h-5 text-orange" />
            Download Report
          </h3>
          <p className="text-sm text-[#6F8D90]">
            Download your ROI calculator results as a PDF or have it sent directly to your email.
          </p>
        </div>

        {/* Middle section - Email input (1/3) */}
        <div className="lg:w-1/3 flex justify-center">
          <div className="relative w-full">
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailStatus === 'error') setEmailStatus('idle');
              }}
              placeholder="Enter your email"
              className={`w-full px-4 py-3 text-sm bg-white border rounded-lg text-[#6F8D90] placeholder:text-[#6F8D90] focus:outline-none focus:ring-2 focus:ring-orange focus:border-transparent ${
                emailStatus === 'error' ? 'border-red-500' : 'border-orange'
              }`}
              disabled={emailStatus === 'loading'}
            />
            {emailStatus === 'success' && (
              <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-emerald-500" />
            )}
          </div>
        </div>

        {/* Right section - Buttons (1/3) */}
        <div className="lg:w-1/3 flex flex-col sm:flex-row gap-2 justify-end">
          {/* Email Report Button - Orange filled */}
          <button
            onClick={handleEmailPdf}
            disabled={emailStatus === 'loading'}
            className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-orange text-white font-medium rounded-lg hover:bg-[#A24E2F] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {emailStatus === 'loading' ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Mail className="w-5 h-5" />
            )}
            {emailStatus === 'loading' ? 'Sending...' : 'Email Report'}
          </button>

          {/* Download PDF Button - Orange border, white background */}
          <button
            onClick={handleDownloadPdf}
            disabled={isDownloading}
            className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-white border-2 border-orange text-orange font-medium rounded-lg hover:bg-orange/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDownloading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Download className="w-5 h-5" />
            )}
            {isDownloading ? 'Generating...' : 'Download PDF'}
          </button>
        </div>
      </div>

      {/* Error/Success Messages */}
      {emailStatus === 'error' && errorMessage && (
        <div className="mt-4 flex items-center gap-2 text-sm text-red-500">
          <AlertCircle className="w-4 h-4" />
          {errorMessage}
        </div>
      )}
      {emailStatus === 'success' && (
        <div className="mt-4 flex items-center gap-2 text-sm text-emerald-500">
          <CheckCircle className="w-4 h-4" />
          Report sent successfully! Check your inbox.
        </div>
      )}
    </Card>
  );
};
